import React, { useCallback, useMemo, useState } from 'react';
import { Container } from '@mui/material';
import PostInput from './ThoughtInput';
import { FeedContainer, IFeedContainerProps } from './FeedContainer';
import { CategorySelector, ICategorySelector } from '../Cards/CategorySelector';
import { EndOfDataMessage } from '../Cards/EndOfDataMessage';
import { IActivityFeedItem } from '../../redux/activity/types';
import { LeftLayerShimmer } from './LeftLayerShimmer';
import SlidingContainer from './SlidingContainer';
import { useAuth } from '../../module/AuthContext';
import { DetailsMenu } from './details.data';
import { isProjectProcessingSelector, mediaForgeMessageSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { useDispatch } from 'react-redux';
import { pauseUpload, resumeUpload, terminateUplaod } from '../../redux/upload/action';
import { getJWtDetails } from '../../utils/AppUtils';

export interface ILeftFeedProps extends Omit<IFeedContainerProps, 'activities'>, Omit<ICategorySelector, 'tags'> {
    endMessageForFollowersActivity: string;
    shouldShowEndMessageForFollowersActivity: boolean;
    endMessageForInterestActivity: string;
    shouldShowEndMessageForInterestActivity: boolean;
    endMessageForChronologicalActivity: string;
    shouldShowEndMessageForChronologicalActivity: boolean;
    startInterestLoad?: boolean;
    interestActivities: IActivityFeedItem[];
    followingActivities: IActivityFeedItem[];
    chronologicalActivities: IActivityFeedItem[];
    startFollowingLoad?: boolean;
    startChronologicalLoad?: boolean;
    shouldShowLoading?: boolean;
    showChronogicalLoading?: boolean;
    onShowOlder?: (category: string) => void;
}
export const LeftFeedLayer: React.FC<ILeftFeedProps> = props => {
    const {
        endMessageForChronologicalActivity,
        endMessageForFollowersActivity,
        endMessageForInterestActivity,
        shouldShowEndMessageForChronologicalActivity,
        shouldShowEndMessageForFollowersActivity,
        shouldShowEndMessageForInterestActivity,
        chronologicalActivities,
        followingActivities,
        interestActivities,
        startInterestLoad,
        startChronologicalLoad,
        startFollowingLoad,
        shouldShowLoading,
        onShowOlder,
        showChronogicalLoading,
    } = props;
    const { isSignedIn } = useAuth();
    const { user } = getJWtDetails();
    const [open, setOpen] = useState(false);
    const [categorySelector, setCategorySelector] = useState<keyof DetailsMenu>();
    const isProjectProcessing = useSelector(isProjectProcessingSelector);
    const { progress, uploadMessage, isPaused } = useSelector(mediaForgeMessageSelector);
    const dispatch = useDispatch();

    const onShowOlderClick = (category: string) => {
        onShowOlder && onShowOlder(category);
    };
    const handleOpenClose = useCallback(() => {
        setOpen(!open);
    }, [open]);

    const renderPostInput = useMemo(
        () =>
            isSignedIn &&
            user && (
                <PostInput
                    user={user}
                    onOpenModal={handleOpenClose}
                    onButtonClick={category => {
                        setCategorySelector(category);
                        handleOpenClose();
                    }}
                />
            ),
        [isSignedIn, user, handleOpenClose],
    );

    const handleOnUploadCancel = () => {
        dispatch(terminateUplaod());
    };

    const handleOnResumeUpload = () => {
        dispatch(resumeUpload(undefined));
    };
    const handleOnPauseUplaod = () => {
        dispatch(pauseUpload('Upload currently paused.'));
    };
    return (
        <Container disableGutters>
            <CategorySelector
                clickable
                tags={[
                    'All',
                    'Auditions',
                    'Jobs',
                    'Contest',
                    'Events',
                    'Videos',
                    'Abuja Jobs',
                    'Lagos Gigs',
                    'Modelling',
                ]}
                {...props}
            />
            {isProjectProcessing && (
                <ProgressBar
                    progress={progress}
                    onCancel={handleOnUploadCancel}
                    uploadMessage={uploadMessage}
                    onResume={handleOnResumeUpload}
                    onPause={handleOnPauseUplaod}
                    isPaused={isPaused}
                    desktopSX={{
                        marginTop: 0,
                        mb: 2,
                        marginLeft: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                    }}
                    mobileSX={{
                        mb: { xs: '10px', sm: '10px' },
                        marginLeft: { xs: '10px', sm: '15px' },
                        paddingLeft: { xs: '10px', sm: '40px' },
                        paddingRight: { xs: '10px', sm: '16px' },
                        overflowX: 'hidden',
                    }}
                />
            )}
            {renderPostInput}

            <SlidingContainer open={open} onClose={handleOpenClose} initialCategory={categorySelector} />

            {startFollowingLoad && !!followingActivities && (
                <FeedContainer activities={followingActivities} {...props} />
            )}
            {shouldShowLoading && (
                <>
                    <LeftLayerShimmer variant="activity" />
                    <LeftLayerShimmer variant="activity" />
                </>
            )}
            {shouldShowEndMessageForFollowersActivity && endMessageForFollowersActivity && (
                <EndOfDataMessage
                    messaage={endMessageForFollowersActivity}
                    showMoreClick={() => onShowOlderClick('following')}
                />
            )}
            {startInterestLoad && !!interestActivities && <FeedContainer activities={interestActivities} {...props} />}
            {shouldShowEndMessageForInterestActivity && endMessageForInterestActivity && (
                <EndOfDataMessage
                    messaage={endMessageForInterestActivity}
                    showMoreClick={() => onShowOlderClick('interest')}
                />
            )}
            {startChronologicalLoad && !!chronologicalActivities && (
                <FeedContainer activities={chronologicalActivities} {...props} />
            )}
            {showChronogicalLoading && (
                <>
                    <LeftLayerShimmer variant="activity" />
                    <LeftLayerShimmer variant="activity" />
                </>
            )}
            {shouldShowEndMessageForChronologicalActivity && endMessageForChronologicalActivity && (
                <EndOfDataMessage
                    messaage={endMessageForChronologicalActivity}
                    showMoreClick={() => onShowOlderClick('chonological')}
                />
            )}
        </Container>
    );
};
